/**
 * mapbox_map_loads — 86baefu3r. Per-tenant Mapbox map-load counter (migration 0099).
 *
 * One row per (tenant, UTC calendar month) holding a running count of Mapbox GL map
 * INSTANTIATIONS. Mapbox bills per map load — one `new mapboxgl.Map(...)` — so that is
 * the only event counted. Not route switches, not pans, not style reloads.
 *
 * web-dispatch creates exactly ONE map per session and parks it between surfaces
 * (`features/map/MapProvider.tsx`, OPEN-006), so a count approximates "dispatcher
 * sessions that opened a map this month" rather than anything finer. Counting
 * `acquire()` calls instead would multiply that by every route switch, which is why the
 * increment is wired to the single construction site and nowhere else.
 *
 * Period is the first of the UTC month. A platform accounting bucket, deliberately not a
 * per-tenant local month: it has to reconcile with how Mapbox bills Paelode, and a local
 * month would make the platform total depend on tenant timezones. DEC-004 is not in
 * tension with that — the console renders the bucket as a LABEL ("October 2026"), so no
 * UTC instant is shown to a user.
 *
 * ── NO RLS, deliberately (Corbin, 2026-10-01 — Option A) ────────────────────────────
 * Note the absence of `pgPolicy` below: that is a decision, not an omission. The console
 * shows per-tenant counts side by side — a cross-tenant read — and the only runtime DB
 * client is `app_user` under RLS, where no policy can gate on role (0033:272). Option A
 * (no RLS, API guard only) was chosen over Option B (RLS + a second elevated client),
 * matching the 0033 / 0036 / 0089 platform-table precedent.
 *
 * Isolation therefore rests on the route layer, and two properties carry it:
 *   - READ is `MANAGE_PLATFORM_CATALOG` — PLATFORM_ADMIN alone of 11 roles.
 *   - WRITE takes NO tenant parameter; the route derives tenant_id from the JWT. With
 *     RLS absent, a body-supplied tenant id would let any dispatcher attribute load to
 *     another tenant, so this is enforced by the route's shape rather than by validation.
 *
 * Acceptable because the contents are a count — no volumes, names, GPS or anything a
 * competitor could use. The same posture on operational data would be wrong.
 *
 * DELETE is REVOKED from app_user, not merely unmentioned. Worth knowing why the
 * distinction matters: 0000_init.sql:677 sets default privileges granting SELECT, INSERT,
 * UPDATE and DELETE on every future table in this schema, so a narrower GRANT in a
 * migration narrows nothing — only an explicit REVOKE does. Verified on a fresh database
 * rather than assumed.
 */

import {
  check,
  index,
  integer,
  date,
  pgTable,
  primaryKey,
  timestamp,
  uuid,
} from 'drizzle-orm/pg-core';
import { sql } from 'drizzle-orm';

// `tenants` is declared in users.ts, not a tenants.ts — there is no such file.
import { tenants } from './users.js';

export const mapboxMapLoads = pgTable(
  'mapbox_map_loads',
  {
    tenantId: uuid('tenant_id')
      .notNull()
      .references(() => tenants.id, { onDelete: 'cascade' }),
    /** First of the UTC month this count covers, e.g. 2026-10-01 for October 2026. */
    periodMonth: date('period_month').notNull(),
    loadCount: integer('load_count').notNull().default(0),
    /**
     * Month bookends, so the console can tell a quiet month from one where reporting
     * stopped mid-month without needing a second table.
     */
    firstLoadedAt: timestamp('first_loaded_at', { withTimezone: true }).notNull().defaultNow(),
    lastLoadedAt: timestamp('last_loaded_at', { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    primaryKey({ name: 'mapbox_map_loads_pkey', columns: [t.tenantId, t.periodMonth] }),
    // Console reads are "all tenants for ONE month", so period leads. The PK's
    // (tenant_id, period_month) order cannot serve that scan.
    index('mapbox_map_loads_period_idx').on(t.periodMonth.desc(), t.tenantId),
    // A negative count can only come from a bug — refuse it at the boundary rather than
    // finding it later in a cost report.
    check('mapbox_map_loads_count_nonneg', sql`${t.loadCount} >= 0`),
  ],
);

export type MapboxMapLoadRow = typeof mapboxMapLoads.$inferSelect;
